import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

const STORAGE_KEY = 'catax_debug_mode';

interface DebugModeContextType {
  /** When true, admin-only debug panels (probability scores, parts state, raw model output) are shown in chat. */
  debugMode: boolean;
  setDebugMode: (value: boolean) => void;
  toggleDebugMode: () => void;
}

const DebugModeContext = createContext<DebugModeContextType | undefined>(undefined);

/**
 * Admin-facing "debug mode" preference, persisted to localStorage so it survives
 * reloads. The toggle that flips it is gated to admins (see DebugModeToggle); the
 * panels that read it are gated to admins as well, so a stale `true` value can
 * never leak model internals to a non-admin user.
 */
/** Like useDebugMode, but undefined outside a DebugModeProvider. */
export const useDebugModeSafe = (): DebugModeContextType | undefined => useContext(DebugModeContext);

export const useDebugMode = (): DebugModeContextType => {
  const context = useContext(DebugModeContext);
  if (context === undefined) {
    throw new Error('useDebugMode must be used within a DebugModeProvider');
  }
  return context;
};

interface DebugModeProviderProps {
  children: ReactNode;
}

export const DebugModeProvider: React.FC<DebugModeProviderProps> = ({ children }) => {
  const [debugMode, setDebugModeState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(debugMode));
    } catch {
      /* localStorage unavailable (private mode) — keep in-memory only */
    }
  }, [debugMode]);

  const setDebugMode = useCallback((value: boolean) => setDebugModeState(value), []);
  const toggleDebugMode = useCallback(() => setDebugModeState(prev => !prev), []);

  return (
    <DebugModeContext.Provider value={{ debugMode, setDebugMode, toggleDebugMode }}>
      {children}
    </DebugModeContext.Provider>
  );
};

export default DebugModeContext;
